import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  FolderKey,
  Loader2,
  AlertCircle,
  Mail,
  RefreshCw,
  ShieldOff,
  UserX,
  Clock,
  CheckCircle2,
  XCircle,
  MapPin,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import {
  GET_SFTP_ACCESS,
  GENERATE_SFTP_ACCESS,
  ROLL_SFTP_KEY,
  RESEND_SFTP_SETUP_LINK,
  CANCEL_SFTP_SETUP_LINK,
  REVOKE_PREVIOUS_SFTP_KEY,
  REVOKE_SFTP_ACCESS,
  type GetSftpAccessData,
  type GetSftpAccessVars,
} from '@/graphql/sftpAccess';

interface SftpAccessCardProps {
  clientId: string;
  /** Defaulted into the recipient field; CET can edit before sending. */
  defaultContactEmail?: string;
}

/**
 * Client SFTP access management.
 *
 * Note what this card deliberately cannot do: show a private key. Clicking
 * "Generate" provisions the AWS Transfer Family account with no key attached and
 * emails the client a single-use link — the keypair is not created until the
 * client opens that link, requests a second-factor code sent to the same address,
 * and enters it. The private half is shown to them once, on a page CET never
 * sees. There is no state of this card, and no mutation behind it, that can
 * reveal key material.
 */
export function SftpAccessCard({ clientId, defaultContactEmail }: SftpAccessCardProps) {
  const refetchQueries = [{ query: GET_SFTP_ACCESS, variables: { organizationId: clientId } }];

  const { data, loading, error } = useQuery<GetSftpAccessData, GetSftpAccessVars>(
    GET_SFTP_ACCESS,
    { variables: { organizationId: clientId } }
  );

  const [generateAccess, { loading: isGenerating }] = useMutation(GENERATE_SFTP_ACCESS, { refetchQueries });
  const [rollKey, { loading: isRolling }] = useMutation(ROLL_SFTP_KEY, { refetchQueries });
  const [resendLink, { loading: isResending }] = useMutation(RESEND_SFTP_SETUP_LINK, { refetchQueries });
  const [cancelLink, { loading: isCancelling }] = useMutation(CANCEL_SFTP_SETUP_LINK, { refetchQueries });
  const [revokePrevious, { loading: isRevoking }] = useMutation(REVOKE_PREVIOUS_SFTP_KEY, { refetchQueries });
  const [revokeAccess, { loading: isRevokingAccess }] = useMutation(REVOKE_SFTP_ACCESS, { refetchQueries });

  // ==================== State ====================
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showRollModal, setShowRollModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showRevokeAccessModal, setShowRevokeAccessModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [formError, setFormError] = useState('');

  // ==================== Derived ====================
  const access = data?.sftpAccess;
  const user = access?.user ?? null;
  const pendingLink = access?.pendingLink ?? null;
  const lastClaim = access?.lastClaim ?? null;
  const keyState = access?.keyState ?? null;
  const graceExpiresAt = access?.previousKeyGraceExpiresAt
    ? new Date(access.previousKeyGraceExpiresAt)
    : null;
  const graceActive = keyState === 'ROTATING' && graceExpiresAt !== null;

  // ==================== Helpers ====================
  const openEmailModal = (which: 'generate' | 'roll' | 'resend') => {
    setEmailInput(
      which === 'resend'
        ? pendingLink?.recipientEmail ?? defaultContactEmail ?? ''
        : defaultContactEmail ?? ''
    );
    setFormError('');
    if (which === 'generate') setShowGenerateModal(true);
    if (which === 'roll') setShowRollModal(true);
    if (which === 'resend') setShowResendModal(true);
  };

  const closeModals = () => {
    setShowGenerateModal(false);
    setShowRollModal(false);
    setShowResendModal(false);
    setShowRevokeModal(false);
    setShowRevokeAccessModal(false);
    setEmailInput('');
    setFormError('');
  };

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const runWithEmail = async (
    action: (email: string) => Promise<unknown>,
    fallbackMessage: string
  ) => {
    if (!isValidEmail(emailInput)) {
      setFormError('Enter a valid email address for the client contact.');
      return;
    }
    try {
      await action(emailInput.trim());
      closeModals();
    } catch (err) {
      const message = err instanceof Error ? err.message : fallbackMessage;
      setFormError(message || fallbackMessage);
    }
  };

  const formatDate = (value: string | Date | null) => {
    if (!value) return '—';
    const date = typeof value === 'string' ? new Date(value) : value;
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const hoursUntil = (date: Date) => {
    const diffMs = date.getTime() - Date.now();
    if (diffMs <= 0) return 'expiring now';
    const hours = Math.round(diffMs / 3600000);
    if (hours < 24) return `in ~${hours}h`;
    return `in ~${Math.round(hours / 24)}d`;
  };

  // ==================== Loading / Error ====================
  if (loading) {
    return (
      <div className="card">
        <div className="card-body flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-chalkboard dark:text-rain-cloud" />
          <span className="ml-2 text-chalkboard dark:text-rain-cloud">Loading SFTP Access...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body flex items-center justify-center py-12 text-rust dark:text-salmon">
          <AlertCircle className="w-5 h-5 mr-2" />
          Failed to load SFTP Access
        </div>
      </div>
    );
  }

  // The BLAST SFTP CloudFormation stack hasn't been deployed for this
  // environment — show that plainly instead of a broken action.
  if (access && !access.configured) {
    return (
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <FolderKey className="w-5 h-5 text-turquoise dark:text-aqua" />
            <h2 className="text-lg font-semibold text-slate dark:text-mist">SFTP Access</h2>
          </div>
        </div>
        <div className="card-body">
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            SFTP is not configured for this environment yet. The BLAST SFTP stack needs to be
            deployed before client credentials can be issued.
          </p>
        </div>
      </div>
    );
  }

  // ==================== Status badge ====================
  const statusBadge = (() => {
    if (pendingLink) {
      return {
        label: pendingLink.mode === 'ROTATE' ? 'Key Roll Pending' : 'Setup Pending',
        icon: Clock,
        className: 'bg-harvest/10 dark:bg-harvest-light/10 text-harvest dark:text-harvest-light',
      };
    }
    if (keyState === 'ROTATING') {
      return {
        label: 'Rotating',
        icon: RefreshCw,
        className: 'bg-harvest/10 dark:bg-harvest-light/10 text-harvest dark:text-harvest-light',
      };
    }
    if (keyState === 'ACTIVE') {
      return {
        label: 'Active',
        icon: CheckCircle2,
        className: 'bg-sage/10 dark:bg-sage-light/10 text-sage dark:text-sage-light',
      };
    }
    if (keyState === 'PENDING_KEY') {
      return {
        label: 'Awaiting Client Setup',
        icon: Clock,
        className: 'bg-harvest/10 dark:bg-harvest-light/10 text-harvest dark:text-harvest-light',
      };
    }
    return {
      label: 'Not Set Up',
      icon: XCircle,
      className: 'bg-pale-gray-3/60 dark:bg-night-pond text-chalkboard dark:text-rain-cloud',
    };
  })();

  const StatusIcon = statusBadge.icon;

  // ==================== Render ====================
  return (
    <>
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <FolderKey className="w-5 h-5 text-turquoise dark:text-aqua" />
                <h2 className="text-lg font-semibold text-slate dark:text-mist">SFTP Access</h2>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium',
                    statusBadge.className
                  )}
                >
                  <StatusIcon className="w-3 h-3" />
                  {statusBadge.label}
                </span>
              </div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">
                Issue SFTP credentials for this client. The client confirms via a code emailed to
                the same address, then generates and receives their own private key — it is never
                shown here.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!user && (
                <button
                  onClick={() => openEmailModal('generate')}
                  className="btn-primary"
                  disabled={isGenerating}
                >
                  <Mail className="w-4 h-4" />
                  Generate SSH Key
                </button>
              )}
              {user && !pendingLink && (
                <button
                  onClick={() => openEmailModal('roll')}
                  className="btn-secondary"
                  disabled={isRolling}
                >
                  <RefreshCw className="w-4 h-4" />
                  Roll the Key
                </button>
              )}
              {graceActive && (
                <button
                  onClick={() => setShowRevokeModal(true)}
                  className="btn-secondary text-rust dark:text-salmon"
                  disabled={isRevoking}
                  title="End the grace window and revoke the previous key immediately"
                >
                  <ShieldOff className="w-4 h-4" />
                  Revoke Previous Key
                </button>
              )}
              {user && (
                <button
                  onClick={() => {
                    setFormError('');
                    setShowRevokeAccessModal(true);
                  }}
                  className="btn-secondary text-rust dark:text-salmon"
                  disabled={isRevokingAccess}
                  title="Delete the client's SFTP account entirely -- their uploaded files are kept"
                >
                  <UserX className="w-4 h-4" />
                  End SFTP Access
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card-body space-y-4">
          {!user && !pendingLink && (
            <p className="text-sm text-chalkboard dark:text-rain-cloud">
              No SFTP access yet. Generating will create the client&apos;s SFTP account and email
              them a one-time link to set up their key.
            </p>
          )}

          {user && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-chalkboard dark:text-rain-cloud">Host</dt>
                <dd className="font-mono text-slate dark:text-mist break-all">
                  {access?.host ?? '—'}
                  {access?.port ? `:${access.port}` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-chalkboard dark:text-rain-cloud">Username</dt>
                <dd className="font-mono text-slate dark:text-mist break-all">{user.username}</dd>
              </div>
              <div>
                <dt className="text-chalkboard dark:text-rain-cloud">Home Directory</dt>
                <dd className="font-mono text-slate dark:text-mist break-all">
                  {user.homeDirectory}
                </dd>
              </div>
              <div>
                <dt className="text-chalkboard dark:text-rain-cloud">Key Issued</dt>
                <dd className="text-slate dark:text-mist">
                  {formatDate(access?.currentKeyImportedAt ?? null)}
                </dd>
              </div>
            </dl>
          )}

          {/* Rotation grace window */}
          {graceActive && graceExpiresAt && (
            <div className="rounded-md border border-harvest/40 bg-harvest/5 dark:border-harvest-light/30 dark:bg-harvest-light/5 p-3">
              <div className="flex items-start gap-2">
                <RefreshCw className="w-4 h-4 mt-0.5 text-harvest dark:text-harvest-light shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-slate dark:text-mist">
                    Previous key still valid until {formatDate(graceExpiresAt)} (
                    {hoursUntil(graceExpiresAt)})
                  </p>
                  <p className="text-chalkboard dark:text-rain-cloud mt-0.5">
                    The client has claimed their new key. The old one keeps working during the grace
                    window so their automated transfers don&apos;t break mid-swap, then it is revoked
                    automatically. Revoke it now if the old key may be compromised.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Where the last key was collected from. A datacenter address here
              means an email sandbox took the link instead of the client. */}
          {lastClaim && !pendingLink && (
            <div className="rounded-md border border-pale-gray-3 dark:border-night-pond p-3">
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 text-chalkboard dark:text-rain-cloud shrink-0" />
                <div>
                  <p className="text-slate dark:text-mist">
                    Key collected {formatDate(lastClaim.claimedAt)} from{' '}
                    <span className="font-mono">{lastClaim.fromIp ?? 'unknown IP'}</span>
                    {lastClaim.fromCountry ? ` (${lastClaim.fromCountry})` : ''}
                  </p>
                  <p className="text-chalkboard dark:text-rain-cloud mt-0.5">
                    Sent to <span className="font-mono">{lastClaim.recipientEmail}</span>. If this
                    location looks wrong, or the client says they never received a key, roll the key
                    to issue a fresh link.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pending setup link */}
          {pendingLink && (
            <div className="rounded-md border border-pale-gray-3 dark:border-night-pond p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 text-sm">
                  <Clock className="w-4 h-4 mt-0.5 text-harvest dark:text-harvest-light shrink-0" />
                  <div>
                    <p className="font-medium text-slate dark:text-mist">
                      {pendingLink.mode === 'ROTATE'
                        ? 'Waiting for the client to claim their replacement key'
                        : 'Waiting for the client to set up their key'}
                    </p>
                    <p className="text-chalkboard dark:text-rain-cloud mt-0.5">
                      Sent to <span className="font-mono">{pendingLink.recipientEmail}</span> ·
                      expires {formatDate(pendingLink.expiresAt)} (
                      {hoursUntil(new Date(pendingLink.expiresAt))})
                    </p>
                    <p className="text-xs text-chalkboard dark:text-rain-cloud mt-1">
                      Requested by {pendingLink.initiatedByEmail}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => openEmailModal('resend')}
                    className="btn-ghost btn-sm"
                    disabled={isResending}
                  >
                    Resend
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await cancelLink({ variables: { setupLinkId: pendingLink.id } });
                      } catch (err) {
                        console.error('Failed to cancel SFTP setup link:', err);
                      }
                    }}
                    className="btn-ghost btn-sm text-rust dark:text-salmon"
                    disabled={isCancelling}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generate */}
      <Modal isOpen={showGenerateModal} onClose={closeModals} title="Generate SFTP Access">
        <div className="space-y-4">
          <p className="text-sm text-slate dark:text-mist">
            This creates the client&apos;s SFTP account and emails them a one-time link. On that page
            they request a 6-digit code, sent to this same address, and enter it to generate their
            key. The private key is shown only to the client, only once — it is never visible here
            and we do not keep a copy.
          </p>
          <p className="text-xs text-chalkboard dark:text-rain-cloud">
            The code is why the link alone isn&apos;t enough: whoever generates the key has to be
            able to read this mailbox.
          </p>
          <EmailField
            value={emailInput}
            onChange={(v) => {
              setEmailInput(v);
              setFormError('');
            }}
            error={formError}
          />
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={closeModals} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() =>
                runWithEmail(
                  (email) =>
                    generateAccess({ variables: { organizationId: clientId, recipientEmail: email } }),
                  'Failed to generate SFTP access.'
                )
              }
              className="btn-primary"
              disabled={!emailInput || isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Setup Link
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Roll */}
      <Modal isOpen={showRollModal} onClose={closeModals} title="Roll the SFTP Key">
        <div className="space-y-4">
          <p className="text-sm text-slate dark:text-mist">
            The client will be emailed a one-time link to generate a replacement key, confirmed with
            a code sent to the same address.
          </p>
          <div className="rounded-md border border-harvest/40 bg-harvest/5 dark:border-harvest-light/30 dark:bg-harvest-light/5 p-3 text-sm text-chalkboard dark:text-rain-cloud">
            The current key keeps working for <strong>96 hours</strong> after the client claims the
            new one, so their scheduled transfers don&apos;t break mid-swap. You can end that window
            early with &quot;Revoke Previous Key&quot;.
          </div>
          <EmailField
            value={emailInput}
            onChange={(v) => {
              setEmailInput(v);
              setFormError('');
            }}
            error={formError}
          />
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={closeModals} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() =>
                runWithEmail(
                  (email) => rollKey({ variables: { organizationId: clientId, recipientEmail: email } }),
                  'Failed to start key rotation.'
                )
              }
              className="btn-primary"
              disabled={!emailInput || isRolling}
            >
              {isRolling ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Send Replacement Link
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Resend */}
      <Modal isOpen={showResendModal} onClose={closeModals} title="Resend Setup Link">
        <div className="space-y-4">
          <p className="text-sm text-slate dark:text-mist">
            A new one-time link will be sent and the previous link will stop working. You can correct
            the recipient address here.
          </p>
          <EmailField
            value={emailInput}
            onChange={(v) => {
              setEmailInput(v);
              setFormError('');
            }}
            error={formError}
          />
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={closeModals} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() =>
                runWithEmail(
                  (email) =>
                    resendLink({
                      variables: { setupLinkId: pendingLink?.id ?? '', recipientEmail: email },
                    }),
                  'Failed to resend the setup link.'
                )
              }
              className="btn-primary"
              disabled={!emailInput || isResending}
            >
              {isResending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Resend Link'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Revoke previous key */}
      <Modal isOpen={showRevokeModal} onClose={closeModals} title="Revoke Previous Key">
        <div className="space-y-4">
          <p className="text-sm text-slate dark:text-mist">
            This immediately stops the client&apos;s previous SSH key from working, ending the grace
            window early.
          </p>
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            If any of their systems are still configured with the old key, those transfers will start
            failing right away. Their new key is unaffected.
          </p>
          {formError && <p className="text-sm text-rust dark:text-salmon">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button onClick={closeModals} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={async () => {
                try {
                  await revokePrevious({ variables: { organizationId: clientId } });
                  closeModals();
                } catch (err) {
                  setFormError(
                    err instanceof Error ? err.message : 'Failed to revoke the previous key.'
                  );
                }
              }}
              className="btn-primary bg-rust hover:bg-rust/90"
              disabled={isRevoking}
            >
              {isRevoking ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Revoking...
                </>
              ) : (
                <>
                  <ShieldOff className="w-4 h-4" />
                  Revoke Now
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* End SFTP access entirely */}
      <Modal isOpen={showRevokeAccessModal} onClose={closeModals} title="End SFTP Access">
        <div className="space-y-4">
          <p className="text-sm text-slate dark:text-mist">
            This deletes the client&apos;s SFTP account and every key on it. Any transfer they have
            scheduled will start failing at the next connection.
          </p>
          <div className="rounded-md border border-lynx dark:border-graphite bg-linen dark:bg-onyx p-3">
            <p className="text-sm text-slate dark:text-mist">
              <strong>Their files are kept.</strong> Everything already uploaded to{' '}
              <span className="font-mono text-xs">{user?.homeDirectory}</span> stays in place — this
              removes access, not data.
            </p>
          </div>
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            You can set them up again later with &quot;Generate SSH Key&quot;. They&apos;ll land back
            in the same folder with their existing files.
          </p>
          {formError && <p className="text-sm text-rust dark:text-salmon">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button onClick={closeModals} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={async () => {
                try {
                  await revokeAccess({ variables: { organizationId: clientId } });
                  closeModals();
                } catch (err) {
                  setFormError(
                    err instanceof Error ? err.message : 'Failed to end SFTP access.'
                  );
                }
              }}
              className="btn-primary bg-rust hover:bg-rust/90"
              disabled={isRevokingAccess}
            >
              {isRevokingAccess ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Ending access...
                </>
              ) : (
                <>
                  <UserX className="w-4 h-4" />
                  End Access
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Recipient address field — editable so CET can send to the client's IT contact. */
function EmailField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
        Send to (client contact)
      </label>
      <input
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g., it@firstnational.com"
        className={cn('input', error && 'border-rust dark:border-salmon')}
      />
      {error && <p className="text-sm text-rust dark:text-salmon mt-1">{error}</p>}
      <p className="text-xs text-chalkboard dark:text-rain-cloud mt-1">
        Often the client&apos;s IT or operations contact rather than the business contact.
      </p>
    </div>
  );
}
